import { useCallback, useMemo } from 'react';
import { Field, FormSpy } from 'react-final-form';
import { FieldArray } from 'react-final-form-arrays';
import { connect } from 'react-redux';

import { ITargetEntity } from 'APIPackage/DictionariesModule/interfaces/ITargetEntity';
import { IDictionarySelectOption } from 'CorePackage/DictionariesModule/interfaces/IDictionarySelectOption';
import { getFieldArrayErrorMessage } from 'CorePackage/MainModule/common/getFieldArrayErrorMessage';
import { IRootState } from 'CorePackage/MainModule/interfaces/IRootState';
import { KnmViolatedRequirementsNpaItem } from 'KnmPackage/KnmViolatedRequirementsNpa/components/KnmViolatedRequirementsNpaItem';
import { KnmSubjectTypesEnum } from 'KnmErknmPackage/ErknmSubjectModule/components/ErknmSubjectInfo';
import { IKnmErknmFormSubject } from 'KnmErknmPackage/MainModule/interfaces/IKnmErknmFormSubject';
import { IKnmErknmFormViolatedRequirementsNpa } from 'KnmErknmPackage/MainModule/interfaces/IKnmErknmFormViolatedRequirementsNpa';
import { KnmCollapse } from 'KnmPackage/MainModule/components/KnmCollapse';
import { IKnmFormRef } from 'KnmPackage/MainModule/interfaces/IKnmFormRef';
import {
  getKnmDataCreated,
  getKnmErknmParticipantPositionsSelectOptions,
  getKnmErknmPositionsSelectOptions,
  getKnmFormRef,
  getTargetEntitySelectOptions,
} from 'KnmPackage/MainModule/selectors/knmSelectors';
import { IKnmPmFormResultDecision } from 'KnmPmPackage/MainModule/interfaces/IKnmPmFormResultDecision';
import { FieldBlock } from 'UIPackage/FormModule/components/FieldBlock';
import { Row } from 'UIPackage/FormModule/components/Row';
import { Button } from 'UIPackage/MainModule/components/Button';
import { ShowMoreButton } from 'UIPackage/MainModule/components/ShowMoreButton/ShowMoreButton';
import { DatePickerField } from 'UIPackage/MainModule/components/DatePickerField';
import { SelectInputField } from 'UIPackage/MainModule/components/SelectInputField';
import { TextInputField } from 'UIPackage/MainModule/components/TextInputField';
import { Textarea } from 'UIPackage/MainModule/components/Textarea';
import { TextareaField } from 'UIPackage/MainModule/components/TextareaField';
import { KnmSubject } from 'KnmPackage/KnmSubjectModule/components/KnmSubject';
import { getAdminFetchSettings } from 'PersonalAccountPackage/AdminModule/selectors/adminSelectors';
import { KnmLateFieldsRemark } from 'KnmPackage/KnmLateFieldsRemarkModule/components/KnmLateFieldsRemark';
import { KnmResultDecisionSubjectTypesEnum } from 'KnmPackage/KnmSubjectModule/enums/KnmResultDecisionSubjectTypesEnum';
import { IKnmForm } from 'KnmPackage/MainModule/interfaces/IKnmForm';
import { getResultDecisionPositionsSelectOptions } from 'functions/getResultDecisionPositionsSelectOptions';

import { resultDecisionSubjectTypeOptions } from './PmResultOfInspectionPmResultDecisions';
import { PmResultOfInspectionResultDecisionControllingSubjectMap } from './PmResultOfInspectionResultDecisionControllingSubjectMap';
import { PmResultOfInspectionResultDecisionsFileInput } from './PmResultOfInspectionResultDecisionsFileInput';
import { PmResultOfInspectionsResultDecisionsSigner } from './PmResultOfInspectionsResultDecisionsSigner';
import { useShowMore } from '../../../hooks/useShowMore';
import { IAdminSetting } from '../../../APIPackage/AdminModule/interfaces/IAdminSetting';
import { isLaterThanAdminSetting } from '../../../CorePackage/MainModule/validators/isLaterThanAdminSetting';
import { AdminSettingCodesEnum } from '../../../APIPackage/AdminModule/enums/AdminSettingCodesEnum';
import { Signers } from '../../../KnmPackage/SignersModule/components/Signers';
import { IDictionaryParticipantPositionSelectOption } from '../../../CorePackage/DictionariesModule/interfaces/IDictionaryParticipantPositionSelectOption';
import { IDictionaryPositionSelectOption } from '../../../CorePackage/DictionariesModule/interfaces/IDictionaryPositionSelectOption';
import { KindResultDecisionCodesEnum } from '../../../APIPackage/CatalogsModule/enums/KindResultDecisionCodesEnum';

interface IPmResultOfInspectionPmResultDecisionsCollapseContentProps {
  id: string;
  isExpandAllInPrescription?: boolean;
  isPaperless: boolean;
  isWatchOnly: boolean;
  lateFieldName: string;
  name: string;
  openViolatedRequirementsNpaModal: () => void;
  pmSubject?: IKnmErknmFormSubject;
  resultIndex: number;
  value: IKnmPmFormResultDecision;
}

interface IPmResultOfInspectionPmResultDecisionsCollapseContentStateProps {
  created: string | null | undefined;
  formRef: IKnmFormRef | undefined;
  participantPositionsSelectOptions: IDictionaryParticipantPositionSelectOption[] | undefined;
  positionsSelectOptions: IDictionaryPositionSelectOption[] | undefined;
  settings: IAdminSetting[] | undefined;
  subjectTypeOptions: IDictionarySelectOption<ITargetEntity>[] | undefined;
}
const PmResultOfInspectionPmResultDecisionsCollapseContentWithoutStore = ({
  id,
  isExpandAllInPrescription,
  isPaperless,
  isWatchOnly,
  lateFieldName,
  name,
  openViolatedRequirementsNpaModal,
  pmSubject,
  resultIndex,
  value,
  created,
  formRef,
  participantPositionsSelectOptions,
  positionsSelectOptions,
  settings,
  subjectTypeOptions,
}: IPmResultOfInspectionPmResultDecisionsCollapseContentProps &
  IPmResultOfInspectionPmResultDecisionsCollapseContentStateProps) => {
  const isNewObject = value.isNew;
  const isControlledPerson = value.subjectType?.value === KnmResultDecisionSubjectTypesEnum.CONTROLLING_SUBJECT;
  const pmSubjectSubjectType = (pmSubject?.subjectType?.label ?? KnmSubjectTypesEnum.UR) as KnmSubjectTypesEnum;

  const violatedRequirementsNpaFieldName = `${name}.violatedMandatoryRequirements`;
  const remove = formRef?.current.mutators.remove;

  const { requirementsNpa, act }: IKnmForm = useMemo(() => formRef?.current.getState().values || {}, [formRef]);

  const actRequirementsNpa =
    ((act?.violatedMandatoryRequirements || requirementsNpa) as IKnmErknmFormViolatedRequirementsNpa[] | undefined) ||
    [];

  const isUseSigners = isLaterThanAdminSetting(
    created,
    settings,
    AdminSettingCodesEnum.ADD_NEW_SIGNER_199_4_1_6_STARTS_FROM,
  );

  const actRequirementsByNpaId = useMemo(() => {
    return new Map(actRequirementsNpa.map(requirement => [requirement.npa.id, requirement]));
  }, [actRequirementsNpa]);

  const fullPositionsSelectOptions = useMemo(
    () =>
      getResultDecisionPositionsSelectOptions(
        value?.kindDecision?.code,
        participantPositionsSelectOptions,
        positionsSelectOptions,
      ),
    [value?.kindDecision?.code, positionsSelectOptions, participantPositionsSelectOptions],
  );

  const removeViolatedRequirementsNpa = useCallback(
    (npaIndex: number) => {
      remove && remove(violatedRequirementsNpaFieldName, npaIndex);
    },
    [remove, violatedRequirementsNpaFieldName],
  );

  const ControllingSubjectComponent = PmResultOfInspectionResultDecisionControllingSubjectMap[pmSubjectSubjectType];

  const subjectTitle = pmSubject
    ? [
        pmSubject?.inn && `ИНН: ${pmSubject.inn}`,
        pmSubject?.ogrn && `ОГРН: ${pmSubject.ogrn}`,
        pmSubject?.organizationName && `Наименование: ${pmSubject.organizationName}`,
      ]
        .filter(Boolean)
        .join(', ')
    : 'Значение отсутствует';

  const getResultDecisionsFounderFieldBlockId = useCallback((fieldId: string) => `${name}.founder.${fieldId}`, [name]);

  if (isPaperless && !value.kindDecision?.value) return null;

  return (
    <>
      {isPaperless && (
        <>
          <Row>
            <FieldBlock placementLabel="left" size="half" color="black" label="Номер решения КНО">
              <TextInputField
                id={`${name}.number`}
                name={`${name}.number`}
                placeholder="Введите номер решения"
                disabled={isWatchOnly}
              />
            </FieldBlock>
            <FieldBlock placementLabel="left" size="half" color="black" label="Дата издания решения">
              <DatePickerField
                id={`${name}.date`}
                name={`${name}.date`}
                placeholder="Выберите дату издания решения"
                disabled={isWatchOnly}
              />
            </FieldBlock>
          </Row>
          <Row margin="none">
            <PmResultOfInspectionResultDecisionsFileInput
              formRef={formRef}
              name={`${name}.attachment`}
              value={value}
              watchOnly={isWatchOnly || !isNewObject}
            />
          </Row>
        </>
      )}
      <Row>
        <FieldBlock
          label="Срок исполнения решения"
          size="col-4"
          id={`${id}.deadline`}
          tooltip={<KnmLateFieldsRemark lateFieldPath={`${lateFieldName}/deadline`} fieldId={`${id}.deadline`} />}
        >
          <DatePickerField name={`${name}.deadline`} disabled={isWatchOnly} />
        </FieldBlock>
      </Row>
      <Row>
        <FieldBlock
          label="Информация о проведении мероприятий по предотвращению причинения вреда (ущерба) охраняемым законом ценностям"
          id={`${id}.information`}
          tooltip={<KnmLateFieldsRemark lateFieldPath={`${lateFieldName}/information`} fieldId={`${id}.information`} />}
        >
          <TextareaField name={`${name}.information`} disabled={isWatchOnly} />
        </FieldBlock>
      </Row>
      {isUseSigners &&
      (value?.kindDecision?.code === KindResultDecisionCodesEnum.D_1 ||
        value?.kindDecision?.code === KindResultDecisionCodesEnum.D_7) ? (
        <Signers
          formRef={formRef}
          hasActOrResultDecisionFirstExtractDateTime={!!value?.firstExtractDateTime}
          hintText="Введите ФИО и должность лица, уполномоченного на принятие решения"
          inspectors={undefined}
          isActOrResultDecisionSigned={value?.signed === true}
          isNewActOrResultDecision={value?.isNew === true}
          isPm={true}
          label="Подпись предписания"
          parentId={id}
          parentName={name}
          positionsSelectOptions={fullPositionsSelectOptions}
        />
      ) : (
        <PmResultOfInspectionsResultDecisionsSigner
          pmResultDecisionId={id}
          name={name}
          formRef={formRef}
          watchOnly={isWatchOnly}
          positionsSelectOptions={fullPositionsSelectOptions}
          lateFieldName={lateFieldName}
        />
      )}

      <Row>
        <FieldBlock
          label="Лицо, в отношении которого принято решение"
          size="half"
          id={`${id}.subjectType`}
          tooltip={<KnmLateFieldsRemark lateFieldPath={`${lateFieldName}/subjectType`} fieldId={`${id}.subjectType`} />}
        >
          <SelectInputField
            name={`${name}.subjectType`}
            disabled={isWatchOnly}
            isClearable={false}
            options={resultDecisionSubjectTypeOptions}
          />
        </FieldBlock>
      </Row>
      <KnmCollapse
        id={`${id}.organizationBlock`}
        theme="fields"
        title={isControlledPerson ? 'Сведения о контролируемом лице' : 'Сведения об учредителе'}
        openByDefault
      >
        <>
          {isControlledPerson && (
            <Row>
              <FieldBlock label="Контролируемое лицо" color="black">
                <Textarea value={subjectTitle} disabled />
              </FieldBlock>
              {pmSubject && <ControllingSubjectComponent pmSubject={pmSubject} formRef={formRef} />}
            </Row>
          )}
          {!isControlledPerson && (
            <KnmSubject
              formRef={formRef}
              watchOnly={isWatchOnly}
              inn={value.founder?.inn}
              ogrn={value.founder?.ogrn}
              snils={value.founder?.snils}
              isFiz={value.founder?.isFiz}
              rafpNza={value.founder?.rafpNza}
              kpp={value.founder?.kpp}
              isSonko={value.founder?.isSonko}
              subjectType={value.founder?.subjectTypePm}
              organizationAutocomplete={value.founder?.organizationAutocomplete}
              checkSubjectResult={value.founder?.checkSubjectResult}
              smevRequestStatus={value.founder?.smevRequestStatus}
              name={`${name}.founder`}
              subjectTypeLabel="Тип учредителя"
              mspCode={value.founder?.mspCode}
              mspRegistryDate={value.founder?.mspRegistryDate}
              getBlockId={getResultDecisionsFounderFieldBlockId}
              subjectTypeOptions={subjectTypeOptions}
              isIt={value.founder?.isPresentInItRegistry}
              itAccreditationResponse={value.founder?.itAccreditationResponse}
              ervkId={value.founder?.ervkId}
            />
          )}
        </>
      </KnmCollapse>

      <FieldBlock
        label="Устраняемые нарушения обязательных требований"
        button={
          <Button
            className="shared-btn-add"
            text="Добавить"
            type="button"
            variant="link"
            color="primary"
            disabled={isWatchOnly}
            onClick={openViolatedRequirementsNpaModal}
          />
        }
      >
        <FormSpy subscription={{ errors: true, touched: true }}>
          {props => {
            const fieldError = getFieldArrayErrorMessage(
              props.errors?.solutionsPm?.[resultIndex]?.violatedMandatoryRequirements,
            );
            const isFieldTouched = props.touched?.places;

            if (isFieldTouched && fieldError) {
              return <p className="shared-input-error shared-row__small-margin">{fieldError}</p>;
            }

            return null;
          }}
        </FormSpy>
        <FieldArray name={violatedRequirementsNpaFieldName}>
          {({ fields }) => {
            const items = fields.value ?? [];
            const indexItems = items.map((_, index) => index);
            const { visibleItems, hasMore, showMore, totalCount, visibleCount } = useShowMore(indexItems, {
              initialCount: 10,
              incrementCount: 10,
            });

            return (
              <>
                {visibleItems.map((npaIndex: number) => {
                  const fieldValue = items[npaIndex] as IKnmErknmFormViolatedRequirementsNpa;
                  const npaName = `${violatedRequirementsNpaFieldName}[${npaIndex}]`;
                  const correspondingActRequirement = actRequirementsByNpaId.get(fieldValue?.npa?.id);

                  if (!fieldValue || !correspondingActRequirement) {
                    return null;
                  }

                  return (
                    <Field key={npaName} name={npaName}>
                      {({ meta }) => {
                        const isFieldTouched = meta.touched;
                        const errorField = meta.error;
                        const filedId = `${id}.violatedMandatoryRequirements[${fieldValue.guid ?? npaIndex}]`;

                        return (
                          <KnmViolatedRequirementsNpaItem
                            value={fieldValue}
                            name={npaName}
                            watchOnly={isWatchOnly}
                            isViolationFieldsWatchOnly={isWatchOnly}
                            onRemove={() => removeViolatedRequirementsNpa(npaIndex)}
                            addMandatory={() => undefined}
                            touched={isFieldTouched}
                            error={errorField}
                            formRef={formRef}
                            isMandatoryDisabled={true}
                            isInsideDecisionsSection
                            actRequirement={correspondingActRequirement}
                            id={filedId}
                            isExpandAll={isExpandAllInPrescription}
                          />
                        );
                      }}
                    </Field>
                  );
                })}
                {hasMore && <ShowMoreButton onClick={showMore} totalCount={totalCount} visibleCount={visibleCount} />}
              </>
            );
          }}
        </FieldArray>
      </FieldBlock>
    </>
  );
};

const mapStateToProps = (state: IRootState) => ({
  created: getKnmDataCreated(state),
  formRef: getKnmFormRef(state),
  participantPositionsSelectOptions: getKnmErknmParticipantPositionsSelectOptions(state),
  positionsSelectOptions: getKnmErknmPositionsSelectOptions(state),
  settings: getAdminFetchSettings(state),
  subjectTypeOptions: getTargetEntitySelectOptions(state),
});

export const PmResultOfInspectionPmResultDecisionsCollapseContent = connect(mapStateToProps)(
  PmResultOfInspectionPmResultDecisionsCollapseContentWithoutStore,
);
